// ===========================================================================
// Lumi Plus / Room Pro / cosmetics / Support Lumi -- the monetization UI panel.
// Adult-only: creating an account requires an 18+ age token. Minors never see a
// purchase. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el, toast } from '../dom.js';
import { renderAuth } from './auth.js';
import {
  COSMETICS,
  PLUS_PRICES,
  ROOM_PRO_PRICE_CENTS,
  // The numbers, not a copy of them. This sentence said "150 people with 8 on
  // camera" long after the caps became 50 and 6 -- a shop page promising a room
  // the server will not open, which is the one kind of stale copy that takes
  // money for it.
  MAX_ROOM_MEMBERS,
  VIDEO_SLOTS,
  ROOM_PRO_CAPACITY,
  ROOM_PRO_VIDEO_SLOTS, formatMoney } from '@lumi/shared';

// The price the panel QUOTES and the price the checkout CHARGES now come from
// the same constant, and the symbol is derived from it. This was a hardcoded
// `$` sitting beside a server that hardcoded 'usd' separately; nothing would
// have caught the two drifting apart, and the first sign would have been
// somebody charged in a currency they were never shown.
const dollars = (cents) => formatMoney(cents);

/**
 * Open the billing panel.
 * @param {import('../account.js').AccountClient} account
 * @param {{ onChange?: (account: object) => void }} [opts]
 */
export function openBilling(account, opts = {}) {
  const onChange = opts.onChange || (() => {});
  // Opened from a room's Promote button: the code of the room to put on the
  // front page. The promotion section is drawn first, for that room only.
  const promoRoom = typeof opts.promoRoom === 'string' && opts.promoRoom ? opts.promoRoom.toUpperCase() : null;
  const body = el('div', { class: 'billing-body' });
  // Whether checkout can actually complete. Asked once when the panel opens,
  // because showing a price and a Buy button that dead-ends on "unavailable"
  // wastes the visitor's time and reads as a broken site. null = not answered
  // yet, and while it is null nothing is offered for sale.
  let checkoutOpen = null;
  // What the server says can be paid with. Null until asked; while it is null
  // no coin is offered, for the same reason no card button is.
  let methods = null;

  const modal = el('div', { class: 'billing-modal' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('Lumi Plus and more') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => dialog.close() }),
    ]),
    body,
    el('div', { class: 'billing-foot muted', text: t('Lumi is free and ad-free. Upgrades are optional and 18+ only. Under-18 accounts cannot be created.') }),
  ]);
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'Lumi Plus, Room Pro, cosmetics, and donations' });

  async function buy(payload, successMsg) {
    const r = await account.buy(payload);
    if (r.redirecting) return;
    if (r.ok) {
      toast(successMsg || 'Purchase complete.');
      onChange(account.account);
      render();
    } else {
      toast(r.error === 'price_not_configured' ? 'Not configured yet.' : 'Checkout unavailable right now.');
    }
  }

  /**
   * Pay with coin: one row of coins under whatever is being bought.
   *
   * The amount on each button is indicative. The binding number is written
   * onto the invoice a moment later, at that moment's price, and held for
   * fifteen minutes -- which is what the screen after this one says.
   */
  function coinRow(order) {
    if (!methods || !methods.crypto || !methods.coins.length) return null;
    return el('div', { class: 'coin-row' }, [
      el('span', { class: 'coin-row-label muted', text: t('or pay with coin:') }),
      ...methods.coins.map((c) =>
        el('button', {
          class: 'btn btn-secondary btn-sm coin-btn',
          text: `${c.code} ${c.amount}`,
          title: `Pay in ${c.name}. Includes a ${(c.markupBps / 100).toFixed(1)}% conversion margin.`,
          onClick: () => startCoinCheckout({ ...order, coin: c.code }),
        }),
      ),
    ]);
  }

  /**
   * The pay-by-coin screen: an address, an amount, and a live status.
   *
   * No transaction id is ever asked for. The address belongs to this invoice
   * and nothing else, so the server matches the payment by where it landed.
   * That is also why the warning matters: sending twice to the same address,
   * or sending after it expires, is a person's problem to sort out, and the
   * screen says so before they send rather than after.
   */
  async function startCoinCheckout(order) {
    const made = await account.cryptoInvoice(order);
    if (!made.ok) {
      toast(
        made.error === 'no_addresses'
          ? 'That coin is temporarily unavailable. Try another.'
          : 'Could not start that payment. Try again shortly.',
      );
      return;
    }
    showInvoice(made.invoice);
  }

  function showInvoice(first) {
    let invoice = first;
    let timer = null;
    const amount = el('code', { class: 'pay-amount', text: `${invoice.amount} ${invoice.coin}` });
    const address = el('code', { class: 'pay-address', text: invoice.address });
    const status = el('p', { class: 'pay-status' });
    const left = el('span', { class: 'pay-expiry muted' });

    const copy = (text, what) =>
      el('button', {
        class: 'btn btn-ghost btn-xs',
        text: t('Copy'),
        onClick: () => navigator.clipboard?.writeText(text).then(() => toast(`${what} copied.`), () => toast(text)),
      });

    const stop = () => {
      clearInterval(timer);
      timer = null;
    };

    function draw() {
      const done = invoice.status === 'paid';
      status.className = `pay-status is-${invoice.status}`;
      status.textContent = done
        ? 'Paid. Thank you. Your purchase is active.'
        : invoice.status === 'seen'
          ? `Payment seen. Waiting for ${invoice.confirmationsNeeded} confirmations (${invoice.confirmations} so far).`
          : invoice.status === 'underpaid'
            ? 'Less arrived than the invoice asked for. Nothing is lost: get in touch through Support and a person will sort it out.'
            : invoice.status === 'expired'
              ? 'This invoice expired. Nothing was sent, so nothing is owed. Start another when you like.'
              : 'Waiting for your payment.';
      const mins = Math.max(0, Math.round((invoice.expiresAt - Date.now()) / 60000));
      left.textContent = done || invoice.status === 'expired' ? '' : `This price holds for about ${mins} more minute${mins === 1 ? '' : 's'}.`;
      if (done) {
        stop();
        onChange(account.account);
      }
    }

    const panel = el('div', { class: 'billing-section pay-panel' }, [
      el('h4', { text: `Pay ${invoice.amount} ${invoice.coin}` }),
      el('p', { class: 'muted', text: t('Send exactly this amount to this address. It belongs to this payment only, so there is nothing else to tell us.') }),
      el('div', { class: 'pay-row' }, [el('span', { class: 'pay-key', text: t('Amount') }), amount, copy(invoice.amount, 'Amount')]),
      el('div', { class: 'pay-row' }, [el('span', { class: 'pay-key', text: t('Address') }), address, copy(invoice.address, 'Address')]),
      left,
      status,
      el('p', { class: 'muted pay-note', text: t('Sending after the price expires, or sending a different amount, still reaches us. It just needs a person to sort out, through Support.') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Back'), onClick: () => { stop(); render(); } }),
    ]);
    body.replaceChildren(panel);
    draw();
    // Polled rather than pushed: this is a page somebody is staring at for a
    // minute or two, and a socket for it would outlive the reason for it.
    timer = setInterval(async () => {
      if (!panel.isConnected) return stop();
      const r = await account.cryptoStatus(invoice.id);
      if (r.ok && r.invoice) {
        invoice = r.invoice;
        draw();
      }
    }, 5000);
  }

  // Shown in place of a purchase control when checkout is not open. It says so
  // plainly rather than letting someone find out by clicking.
  function closedNotice(extra) {
    return el('div', { class: 'buy-closed' }, [
      el('strong', { text: t('Not open yet. ') }),
      el('span', { text: `Lumi is free and fully usable without any of this.${extra ? ` ${extra}` : ''}` }),
    ]);
  }

  function authForm() {
    // Shared form: age verification runs inline during account creation (selfie if
    // needed, or a reused/stored proof), so there is no "go verify elsewhere" wall.
    return el('div', { class: 'billing-section' }, [
      renderAuth(account, () => {
        onChange(account.account);
        render();
      }),
    ]);
  }

  function plusSection() {
    if (account.isPlus) {
      return el('div', { class: 'billing-section' }, [
        el('h4', { text: t('Lumi Plus') }),
        el('div', { class: 'plus-active', text: t('You are a Plus member. Thank you for keeping Lumi independent and ad-free.') }),
      ]);
    }
    return el('div', { class: 'billing-section' }, [
      el('h4', { text: t('Lumi Plus') }),
      el('p', { class: 'muted', text: t('Priority queue, a higher video bitrate, cosmetic flair, and a Plus mark. No ads, no data sale, ever.') }),
      checkoutOpen && methods?.card
        ? el('div', { class: 'buy-row' }, [
            el('button', { class: 'btn btn-primary btn-sm', text: `Monthly ${dollars(PLUS_PRICES.monthlyCents)}`, onClick: () => buy({ kind: 'plus', sku: 'monthly' }, 'Welcome to Lumi Plus!') }),
            el('button', { class: 'btn btn-secondary btn-sm', text: `Yearly ${dollars(PLUS_PRICES.yearlyCents)}`, onClick: () => buy({ kind: 'plus', sku: 'yearly' }, 'Welcome to Lumi Plus!') }),
          ])
        : null,
      coinRow({ kind: 'plus', sku: 'monthly' }),
      !checkoutOpen || (!methods?.card && !methods?.crypto)
        ? closedNotice(`Plus will be ${dollars(PLUS_PRICES.monthlyCents)} a month or ${dollars(PLUS_PRICES.yearlyCents)} a year.`)
        : null,
    ]);
  }

  function cosmeticsSection() {
    // `cosmetics` is what may be WORN (bought, or included with Plus);
    // `ownedCosmetics` is what was actually paid for. The difference is worth
    // showing: a member should be able to see what they keep if they cancel.
    const owned = new Set(account.cosmetics);
    const bought = new Set(account.account?.ownedCosmetics || []);
    const settings = account.settings;
    const items = COSMETICS.map((c) => {
      const isOwned = owned.has(c.id);
      const viaPlus = isOwned && !bought.has(c.id);
      const applied = (c.kind === 'orb' && settings.orbSkin === c.id) || (c.kind === 'theme' && settings.theme === c.id);
      const action = isOwned
        ? el('button', {
            class: `btn btn-sm ${applied ? 'btn-ghost' : 'btn-primary'}`,
            text: applied ? 'Applied' : 'Apply',
            onClick: async () => {
              const patch = c.kind === 'orb' ? { orbSkin: applied ? '' : c.id } : { theme: applied ? '' : c.id };
              await account.saveSettings(patch);
              onChange(account.account);
              render();
            },
          })
        : checkoutOpen
          ? el('button', { class: 'btn btn-primary btn-sm', text: `Buy ${dollars(c.priceCents)}`, onClick: () => buy({ kind: 'cosmetic', cosmeticId: c.id, amountCents: c.priceCents }, 'Cosmetic unlocked.') })
          : el('span', { class: 'muted', text: dollars(c.priceCents) });
      return el('div', { class: 'cosmetic' }, [
        el('span', { class: 'cosmetic-name', text: c.name }),
        viaPlus ? el('span', { class: 'cosmetic-note', text: t('With Plus') }) : null,
        action,
      ].filter(Boolean));
    });
    return el('div', { class: 'billing-section' }, [el('h4', { text: t('Cosmetics shop') }), el('div', { class: 'cosmetic-grid' }, items)]);
  }

  function roomProSection() {
    const code = el('input', { type: 'text', placeholder: t('Vanity code (e.g. LOUNGE)'), maxlength: '12', aria: { label: 'Vanity code for the room to upgrade' } });
    const owned = (account.roomPro || []).map((r) =>
      el('div', { class: 'pro-room' }, [
        el('span', { text: `${r.name || 'Pro room'}, code ${r.code}` }),
        el('span', { class: 'pill', text: r.status }),
      ]),
    );
    return el('div', { class: 'billing-section' }, [
      el('h4', { text: t('Room Pro') }),
      el('p', { class: 'muted', text: `Make a group room persistent and larger, up to ${ROOM_PRO_CAPACITY} people with ${ROOM_PRO_VIDEO_SLOTS} on camera at once, instead of ${MAX_ROOM_MEMBERS} and ${VIDEO_SLOTS}, with a vanity code and theming. ${dollars(ROOM_PRO_PRICE_CENTS)}/mo.` }),
      checkoutOpen
        ? el('div', { class: 'buy-row' }, [
            code,
            el('button', {
              class: 'btn btn-primary btn-sm',
              text: t('Upgrade room'),
              onClick: () => {
                const c = code.value.trim().toUpperCase();
                if (!/^[A-Z0-9]{3,12}$/.test(c)) {
                  toast('Pick a code: 3-12 letters or digits.');
                  return;
                }
                buy({ kind: 'room_pro', roomCode: c }, `Room ${c} is now Pro.`);
              },
            }),
          ])
        : closedNotice('Rooms you already upgraded keep working.'),
      owned.length ? el('div', { class: 'pro-rooms' }, owned) : null,
    ].filter(Boolean));
  }

  /**
   * Put a room on the front page.
   *
   * Four presets and a custom count, a start now or on a date, the price
   * from the server before anything is paid, and then card or coin. The
   * server decides the price and holds the slot; this only asks and shows.
   */
  function promoSection(roomCode) {
    const PRESETS = [
      ['day', 'A day'],
      ['weekend', 'A weekend'],
      ['week', 'A week'],
      ['month', 'A month'],
      ['custom', 'Custom'],
    ];
    let preset = 'week';
    let startLater = false;
    const days = el('input', { type: 'number', min: '1', max: '90', value: '14', 'aria-label': 'Custom length in days' });
    days.hidden = true;
    const startAt = el('input', { type: 'datetime-local', 'aria-label': 'Start date and time, in your time zone' });
    startAt.hidden = true;
    const price = el('strong', { class: 'promo-price', text: '' });
    const when = el('span', { class: 'muted promo-when', text: '' });
    const note = el('p', { class: 'muted promo-note', text: '' });
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const fmt = (ms) => new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    const order = () => ({
      kind: 'promo',
      roomCode,
      preset,
      days: preset === 'custom' ? Number(days.value) : undefined,
      startsAt: startLater && startAt.value ? new Date(startAt.value).getTime() : undefined,
      timeZone: tz,
    });

    async function quote() {
      const o = order();
      const q = await account.promoQuote({ preset: o.preset, days: o.days, startsAt: o.startsAt, timeZone: o.timeZone });
      if (!q.ok) {
        price.textContent = '';
        when.textContent = REASON[q.error] || 'That length or start does not work.';
        return;
      }
      price.textContent = dollars(q.priceCents);
      when.textContent = `${fmt(q.startsAt)} to ${fmt(q.until)}, your time`;
    }

    const REASON = {
      not_your_room: 'Only the host of this room can promote it, signed in to their own account.',
      already_promoted: 'This room is already promoted or booked. It can be promoted again when that ends.',
      no_slots: 'Every front-page slot is taken for some of that time. Pick another time or a later start.',
      minor_band: 'Teen rooms are never on the front page.',
      standing_room: 'This is one of the rooms Lumi keeps open. It is already on the page.',
      community_room: 'A community room is private by design, so it cannot be put on the front page.',
      room_not_found: 'Lumi cannot find this room right now. Open it, then try again.',
      code_taken: 'Somebody else is in the middle of buying this slot. Try again in a few minutes.',
      bad_time_zone: 'This browser did not say what time zone it is in, so a weekend cannot be placed. Pick a start date instead.',
      start_in_past: 'That start has already gone by. Pick a time still to come, or start now.',
      start_too_far: 'That is too far ahead. A promotion can be booked up to 60 days out.',
      bad_days: 'Custom length is whole days, from 1 to 90.',
      price_not_configured: 'Not configured yet.',
    };

    const presetBtns = PRESETS.map(([key, label]) => el('button', {
      class: `btn btn-ghost btn-sm preset${key === preset ? ' on' : ''}`,
      type: 'button',
      text: label,
      aria: { pressed: String(key === preset) },
      dataset: { preset: key },
      onClick: () => {
        preset = key;
        for (const b of presetBtns) {
          const on = b.dataset.preset === key;
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', String(on));
        }
        days.hidden = key !== 'custom';
        quote();
      },
    }));
    const startBtns = [['now', 'Start now'], ['later', 'Start on a date']].map(([key, label]) => el('button', {
      class: `btn btn-ghost btn-sm preset${(key === 'later') === startLater ? ' on' : ''}`,
      type: 'button',
      text: label,
      aria: { pressed: String((key === 'later') === startLater) },
      dataset: { start: key },
      onClick: () => {
        startLater = key === 'later';
        for (const b of startBtns) {
          const on = (b.dataset.start === 'later') === startLater;
          b.classList.toggle('on', on);
          b.setAttribute('aria-pressed', String(on));
        }
        startAt.hidden = !startLater;
        if (startLater && !startAt.value) {
          const d = new Date(Date.now() + 60 * 60 * 1000);
          d.setMinutes(0, 0, 0);
          const pad = (n) => String(n).padStart(2, '0');
          startAt.value = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        }
        quote();
      },
    }));
    days.addEventListener('change', quote);
    startAt.addEventListener('change', quote);

    async function payByCard() {
      note.textContent = '';
      const r = await account.buy(order());
      if (r.redirecting) return;
      if (r.ok) {
        toast('Paid. Your room is on the front page for the time you chose.');
        onChange(account.account);
        render();
        return;
      }
      note.textContent = REASON[r.error] || 'Checkout unavailable right now.';
    }

    // Coin, for this order. The row under Plus shows an indicative amount at
    // a fixed price; here the price varies with the preset, so the buttons
    // name the coin and the binding amount is written on the invoice a
    // moment later, which is what the next screen says.
    function coinButtons() {
      if (!methods || !methods.crypto || !methods.coins.length) return null;
      return el('div', { class: 'coin-row' }, [
        el('span', { class: 'coin-row-label muted', text: t('or pay with coin:') }),
        ...methods.coins.map((c) =>
          el('button', {
            class: 'btn btn-secondary btn-sm coin-btn',
            text: c.code,
            title: `Pay in ${c.name}. The exact amount is shown on the next screen and held for fifteen minutes.`,
            onClick: async () => {
              note.textContent = '';
              const made = await account.cryptoInvoice({ ...order(), coin: c.code });
              if (!made.ok) {
                note.textContent = REASON[made.error] || (made.error === 'no_addresses' ? 'That coin is temporarily unavailable. Try another.' : 'Could not start that payment. Try again shortly.');
                return;
              }
              showInvoice(made.invoice);
            },
          }),
        ),
      ]);
    }

    quote();
    return el('div', { class: 'billing-section promo-section' }, [
      el('h4', { text: `Promote room ${roomCode}` }),
      el('p', { class: 'muted', text: t('A row on the front page, marked Promoted, with a way straight in. The room stays open the whole time, even when nobody is in it. Adults only; six slots at a time.') }),
      el('div', { class: 'promo-presets' }, presetBtns),
      el('div', { class: 'promo-presets' }, [...startBtns, startAt, days]),
      el('p', { class: 'promo-quote' }, [price, ' ', when]),
      checkoutOpen
        ? el('div', { class: 'buy-row' }, [
            el('button', { class: 'btn btn-primary btn-sm', text: t('Pay by card'), onClick: payByCard }),
          ])
        : closedNotice(''),
      checkoutOpen ? coinButtons() : null,
      note,
      el('p', { class: 'muted', text: '' }, [
        el('a', { href: '/promote-your-room', target: '_blank', rel: 'noopener', text: t('How promoting a room works') }),
      ]),
    ].filter(Boolean));
  }

  function donateSection() {
    const amt = el('input', { type: 'number', placeholder: t('Amount (USD)'), min: '1', value: '5', aria: { label: 'Amount to donate, in US dollars' } });
    return el('div', { class: 'billing-section' }, [
      el('h4', { text: t('Support Lumi') }),
      el('p', { class: 'muted', text: t('Keep an independent, ad-free, privacy-first service alive. Thank you.') }),
      checkoutOpen ? el('div', { class: 'buy-row' }, [
        amt,
        el('button', {
          class: 'btn btn-primary btn-sm',
          text: t('Donate'),
          onClick: () => {
            const cents = Math.round(Number(amt.value) * 100);
            if (!cents || cents < 100) {
              toast('Minimum donation is $1.');
              return;
            }
            buy({ kind: 'donation', amountCents: cents }, 'Thank you for supporting Lumi!');
          },
        }),
      ]) : closedNotice('When it opens, this is how you can chip in.'),
    ]);
  }

  function render() {
    body.replaceChildren();
    if (!account.loggedIn) {
      body.append(
        el('p', {
          class: 'muted',
          text: promoRoom
            ? t('Sign in or create an account (18+) to put your room on the front page. Lumi stays free either way.')
            : t('Sign in or create an account (18+) to go Plus, upgrade a room, or grab cosmetics. Lumi stays free either way.'),
        }),
        authForm(),
      );
      return;
    }
    body.append(
      ...[
        el('div', { class: 'billing-account' }, [
          el('span', { text: `Signed in as ${account.account.username}` }),
          account.isPlus ? el('span', { class: 'plus-badge', text: 'PLUS' }) : null,
          el('button', { class: 'btn btn-ghost btn-sm', text: t('Sign out'), onClick: () => { account.logout(); onChange(null); render(); } }),
        ].filter(Boolean)),
        // Opened from a room: what they came for goes first.
        promoRoom ? promoSection(promoRoom) : null,
        plusSection(),
        cosmeticsSection(),
        roomProSection(),
        donateSection(),
      ].filter(Boolean),
    );
  }

  render();
  fetch('/api/billing/enabled')
    .then((r) => r.json())
    .then(({ enabled }) => {
      checkoutOpen = !!enabled;
      render();
    })
    .catch(() => {
      checkoutOpen = false; // unreachable -> assume closed rather than offer a dead button
      render();
    });
  // Which of the two kinds of payment are switched on, and which coins have
  // both a price and an address to give. Priced against a month of Plus,
  // which is the commonest thing on this panel. Never offered until answered.
  account
    .payMethods(PLUS_PRICES.monthlyCents)
    .then((m) => {
      methods = m;
      render();
    })
    .catch(() => {
      methods = { card: true, crypto: false, coins: [] };
      render();
    });
  return modal;
}

export default { openBilling };
